import {
	AssetTreePicker,
	ErrorModal,
	MesfModal,
	searchAssets,
} from "@dexteel/mesf-core";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import {
	Alert,
	Button,
	CircularProgress,
	type DialogProps,
	Grid2,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTaskListAreas } from "../hooks/useTaskListAreas";
import type { DefaultArea } from "../models/TaskListArea";
import { setTaskListArea } from "../repositories/TaskListAreasRepository";

type Props = {
	/** The default area the new section is added to; null keeps the dialog closed. */
	defaultArea: DefaultArea | null;
	defaultAreas: DefaultArea[];
	onHide: (savedMessage?: string) => void;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const AddTaskListArea = ({
	defaultArea,
	defaultAreas,
	onHide,
}: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const open = !!defaultArea;
	const [assetId, setAssetId] = useState<number | null>(null);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	useEffect(() => {
		if (open) setAssetId(null);
	}, [open]);

	// Parent of every asset, to tell whether the pick sits inside the default area
	const { data: assetsData } = searchAssets();
	const parentOf = useMemo(() => {
		const map = new Map<number, number | null>();
		for (const a of (Array.isArray(assetsData) ? assetsData : []) as Asset[]) {
			map.set(a.AssetId, a.ParentAssetId);
		}
		return map;
	}, [assetsData]);

	const chainOf = (id: number) => {
		const chain: number[] = [];
		let current: number | null | undefined = id;
		while (current !== null && current !== undefined && chain.length < 200) {
			chain.push(current);
			current = parentOf.get(current);
		}
		return chain;
	};

	const chain = assetId !== null ? chainOf(assetId) : [];
	const isInside = !!defaultArea && chain.includes(defaultArea.AssetId);
	const shownFor = defaultAreas
		.filter((d) => chain.includes(d.AssetId))
		.map((d) => d.AssetName);

	// The same read the screen uses, for the picked asset: it says what adding it would change
	const { data, isFetching } = useTaskListAreas(
		assetId,
		open && assetId !== null && isInside,
	);
	const summary =
		isInside && data?.summary?.SelectedAssetId === assetId
			? data.summary
			: null;
	const areasInside = summary ? (data?.areas ?? []) : [];

	const name = summary?.SelectedAssetName ?? "";
	const inAreasInside = areasInside.reduce((n, a) => n + a.OpenWorkOrders, 0);
	const willShow = summary ? summary.TotalOpenWorkOrders - inAreasInside : 0;
	const startShowing = summary?.UncoveredOpenWorkOrders ?? 0;
	const moveFromAbove = willShow - startShowing;

	const onSubmit = async () => {
		if (assetId === null) return;
		setIsSubmitLoading(true);
		const resp = await setTaskListArea(assetId, true);
		setIsSubmitLoading(false);

		if (resp.ok) {
			onHide(`${name} was added to the task list of ${defaultArea?.AssetName}`);
		} else {
			setError(resp.message);
		}
	};

	return (
		<>
			<MesfModal
				title="ADD TASK LIST AREA"
				open={open}
				maxWidth={maxWidth}
				handleClose={() => onHide()}
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={12}>
							<Typography variant="body2">
								Pick an asset inside <b>{defaultArea?.AssetName}</b>. It becomes
								a section of its task list and includes everything below it.
							</Typography>
						</Grid2>
						<Grid2 size={12}>
							<AssetTreePicker
								label="Asset"
								value={assetId}
								onChange={(value) => setAssetId(value)}
							/>
						</Grid2>

						{assetId !== null && !isInside && parentOf.size > 0 && (
							<Grid2 size={12}>
								<Alert severity="warning">
									That asset is not inside {defaultArea?.AssetName}, so it would
									not show on its task list. Pick an asset below it.
								</Alert>
							</Grid2>
						)}

						{isInside && !summary && isFetching && (
							<Grid2 size={12} sx={{ textAlign: "center" }}>
								<CircularProgress size="1.5rem" />
							</Grid2>
						)}

						{summary?.SelectedIsArea && (
							<Grid2 size={12}>
								<Alert severity="info">
									{name} is already a task list area.
								</Alert>
							</Grid2>
						)}

						{summary && !summary.SelectedIsArea && (
							<Grid2 size={12}>
								<Typography variant="body2" sx={{ mb: 1 }}>
									Its section will list{" "}
									<b>{plural(willShow, "open work order")}</b> from{" "}
									<b>{name}</b> and the {plural(summary.AssetsBelow, "asset")}{" "}
									below it.
								</Typography>
								{summary.NearestAreaAboveName && moveFromAbove > 0 && (
									<Alert severity="info" sx={{ mb: 1 }}>
										Those {plural(moveFromAbove, "work order")} leave the{" "}
										<b>{summary.NearestAreaAboveName}</b> section, which {name}{" "}
										sits inside.
									</Alert>
								)}
								{startShowing > 0 && (
									<Typography variant="body2" sx={{ mb: 1 }}>
										{plural(startShowing, "open work order")} that no task list
										shows today start showing.
									</Typography>
								)}
								{shownFor.length > 0 && (
									<Typography variant="body2" sx={{ color: "#666" }}>
										It will show for everyone whose default area is{" "}
										{shownFor.join(", ")}.
									</Typography>
								)}
							</Grid2>
						)}
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Grid2 justifyContent="flex-end" size={12} width={"100%"}>
						<Grid2 container justifyContent="flex-end" spacing={1}>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={
										isSubmitLoading || !summary || summary.SelectedIsArea
									}
									variant="contained"
									color="primary"
									onClick={onSubmit}
								>
									Add
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
